import type { ReactNode } from "react";
import { useState } from "react";
import {
  Card,
  CardHeader,
  Skeleton,
  Avatar,
  Badge,
  Typography,
  CardContent,
  Stack,
  Grid,
  Link,
  IconButton,
  Tooltip,
  cardContentClasses,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import type { User } from "@sdeapps/react-core";
import { graphService as coreGraphService } from "@sdeapps/react-core";
import MailSpan from "components/Span/MailSpan";
import TelephoneSpan from "components/Span/TelephoneSpan";
import type { Agent, Presence } from "models/ContactsMetiers";
import { PresenceIcon } from "./PresenceIcon";
import PhoneIcon from "@mui/icons-material/Phone";
import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import ChatOutlinedIcon from "@mui/icons-material/ChatOutlined";
import CloseIcon from "@mui/icons-material/Close";
import BusinessIcon from "@mui/icons-material/Business";
import useSWR from "swr";
import { createSwrConfiguration } from "utils";

interface ContactMetierUser extends User {
  mobilePhone?: string;
  businessPhones?: Array<string>;
  onPremisesExtensionAttributes?: {
    /** Attribut du numéro interne de l'agent */
    extensionAttribute1?: string;
  };
}

interface ContactMetierCardProps {
  contactMetierAgent: Agent;
  presence?: Presence;
  isPresenceLoading?: boolean;
  onClose?: () => void;
}

export function ContactMetierCard({
  contactMetierAgent,
  presence,
  isPresenceLoading = false,
  onClose,
}: Readonly<ContactMetierCardProps>): ReactNode {
  const [avatar, setAvatar] = useState<string>("");
  const [agent, setAgent] = useState<ContactMetierUser>();

  const { data, isLoading } = useSWR(
    `/getUserById/${contactMetierAgent.id}`,
    async () => {
      const agentTask = coreGraphService.getUser(contactMetierAgent.id, [
        "mobilePhone",
        "businessPhones",
        "onPremisesExtensionAttributes",
      ]);
      const photoTask = coreGraphService.getUserPhoto(contactMetierAgent.id, "48x48");
      const [agent, photo] = await Promise.all([agentTask, photoTask]);
      return { agent, photo };
    },
    createSwrConfiguration()
  );

  if (data != null && data.agent?.id !== agent?.id) {
    setAgent(data.agent);
    setAvatar(data.photo);
  }

  return (
    <Card variant="outlined" sx={{ boxShadow: 1, maxWidth: "500px" }}>
      <CardHeader
        avatar={
          isLoading ? (
            <Skeleton variant="circular">
              <Avatar />
            </Skeleton>
          ) : (
            <Badge
              overlap="circular"
              badgeContent={
                <PresenceIcon fontSize={18} presence={presence} isLoading={isPresenceLoading} />
              }
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
              <Avatar src={avatar}>
                {contactMetierAgent.nom.substring(0, 1).toUpperCase()}
                {contactMetierAgent.prenom.substring(0, 1).toUpperCase()}
              </Avatar>
            </Badge>
          )
        }
        title={
          <Typography sx={{ color: "info.main", fontWeight: 600 }}>
            {agent?.displayName ?? `${contactMetierAgent.nom} ${contactMetierAgent.prenom}`}
          </Typography>
        }
        subheader={isLoading ? <Skeleton /> : agent?.jobTitle}
        action={
          <IconButton onClick={onClose}>
            <Tooltip title="Fermer" placement="bottom">
              <CloseIcon color="error" />
            </Tooltip>
          </IconButton>
        }
      />
      <CardContent sx={{ pt: 0 }}>
        <Stack direction="column" spacing={1}>
          {isPresenceLoading && (
            <Grid container spacing={1} sx={{ alignItems: "center" }}>
              <Skeleton variant="circular" width={22} height={22} />
              <Grid size="grow">
                <Skeleton />
              </Grid>
            </Grid>
          )}
          <Stack direction="row" spacing={1}>
            <PresenceIcon fontSize="small" presence={presence} isLoading={isPresenceLoading} />
            {presence != null &&
              // L'API étant en beta, on teste explicitement pour vérifier que la valeur existe bien
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
              (presence?.outOfOfficeSettings?.isOutOfOffice === true ? (
                <Typography color="error" fontWeight={600}>
                  Absent
                </Typography>
              ) : (
                <Typography color="success" fontWeight={600}>
                  Présent
                </Typography>
              ))}
          </Stack>

          {presence != null &&
            // L'API étant en beta, on teste explicitement pour vérifier que la valeur existe bien
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-boolean-literal-compare
            presence?.outOfOfficeSettings?.isOutOfOffice === true &&
            presence.outOfOfficeSettings.message != null && (
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {presence.outOfOfficeSettings.message.trim()}
              </Typography>
            )}
        </Stack>
      </CardContent>

      <CardContent sx={{ pt: 0, [`&.${cardContentClasses.root}:last-child`]: { pb: 2 } }}>
        {isLoading && <Skeleton />}
        {isLoading && <Skeleton />}
        <Stack direction="column">
          {agent?.mobilePhone != null && (
            <Stack direction="row" spacing={1}>
              <PhoneIcon fontSize="small" sx={{ color: grey[600] }} />
              <TelephoneSpan hasLink telephone={agent?.mobilePhone} />
            </Stack>
          )}
          {agent?.businessPhones?.map((businessPhone: string) => (
            <Stack key={businessPhone} direction="row" spacing={1}>
              <PhoneIcon fontSize="small" sx={{ color: grey[600] }} />
              <TelephoneSpan hasLink telephone={businessPhone} />
            </Stack>
          ))}
          {agent?.onPremisesExtensionAttributes?.extensionAttribute1 != null &&
            agent?.onPremisesExtensionAttributes?.extensionAttribute1 !== "" && (
              <Stack direction="row" spacing={1}>
                <BusinessIcon fontSize="small" sx={{ color: grey[600] }} />
                <TelephoneSpan
                  hasLink
                  telephone={agent?.onPremisesExtensionAttributes.extensionAttribute1}
                />
              </Stack>
            )}
          {agent?.mail != null && (
            <Stack direction="row" spacing={1}>
              <AlternateEmailIcon fontSize="small" sx={{ color: grey[600] }} />
              <MailSpan mail={agent?.mail} />
            </Stack>
          )}
          {agent?.mail != null && (
            <Stack direction="row" spacing={1}>
              <ChatOutlinedIcon fontSize="small" sx={{ color: grey[600] }} />
              <Link href={`msteams://teams.microsoft.com/l/chat/0/0?users=${agent?.mail}`}>
                Contacter via Teams
              </Link>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}
